<template>
  <div
    ref="collapseRef"
    :id="id"
    class="collapse"
    :class="{
      'collapse--expanded': isExpanded,
      'collapse--expandable': body.hasContent,
      'collapse--disabled': disabled,
    }"
  >
    <div class="collapse__header relative" @click="toggle">
      <div class="collapse__header-inner br-12 flex items-center">
        <div class="flex items-start justify-between py-20 flex-1" :class="{'mb-db': mobileDisplayBlock}">
          <div class="collapse__header-title flex flex-column items-start">
            <slot name="title" />
          </div>
          <div
            v-if="body.hasContent"
            class="collapse__header-button flex items-center"
            :class="{'mb-mt-4': mobileDisplayBlock}"
          >
            <span class="fs-12 lh-14 fw-700 underline mr-12" :class="!isExpanded ? 'black' : 'gray-dark'">
              <slot name="icon" v-if="!isExpanded" />
              <slot name="icon-expanded" v-else />
            </span>
            <span class="icon">
              <img :src="`${$assetsCdn}/icons/icon-chevron-right.svg`" alt="accordion" />
            </span>
          </div>
        </div>
      </div>
    </div>

    <transition
      :data-key="body.dataKey"
      name="slide"
      @before-enter="collapse"
      @enter="expand"
      @after-enter="afterEnter"
      @before-leave="expand"
      @leave="collapse"
      @after-leave="afterLeave"
    >
      <div v-show="isExpanded" ref="bodyRef" class="collapse__body">
        <div ref="bodyContentRef" class="collapse__body-content">
          <slot name="content" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue'
import { useCollapse } from './useCollapse'

export default {
  name: 'Collapse',
  props: {
    expanded: {
      type: Boolean,
      default: false,
    },
    triggerExpand: {
      type: Boolean,
      default: false,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    mobileDisplayBlock: {
      default: false,
      type: Boolean,
    },
    index: {
      default: '',
      type: [String, Number],
    },
    id: {
      default: '',
      type: [String, Number],
    },
  },
  emits: ['heapTrack'],
  setup(props, { slots, emit }) {
    const idPanel = `panel-${props.index}`
    const cachedStyles = ref(null)
    const duration = ref(350)
    const easing = ref('ease-in-out')
    const collapseRef = ref(null)
    const bodyRef = ref(null)
    const bodyContentRef = ref(null)
    const { panelExpanded, togglePanelExpandedStatus, setPanelExpandedStatus } = useCollapse()
    const body = computed(() => ({
      hasContent:
        slots.content && slots.content()[0].children.length > 0,
      dataKey: props.index,
    }))
    const idGroup = computed(() => {
      return (
        collapseRef.value?.parentElement?.getAttribute('data-id-group') || ''
      )
    })
    const isExpanded = computed(
      () => panelExpanded(idGroup.value, idPanel).value && body.value.hasContent,
    )
    const transition = computed(() => {
      let transitions = [];
      Object.keys(cachedStyles.value).forEach((key) => {
        transitions.push(
          `${convertToCssProperty(key)} ${duration.value}ms ${easing.value}`
        );
      });
      return transitions.join(", ");
    })
    const toggle = () => {
      if (!body.value.hasContent) return
      if (!isExpanded.value) emit('heapTrack')
      togglePanelExpandedStatus(idGroup.value, idPanel)
    }
    const collapse = (element) => {
      detectAndCacheDimensions(element)
      hideOverflow(element)
      forceRepaint(element)
      setTransition(element)
      setClosedDimensions(element)
    }
    const afterLeave = (element) => {
      unsetTransition(element)
      unsetOverflow(element)
      unsetDimensions(element)
      clearCachedDimensions()
    }
    const expand = (element) => {
      detectAndCacheDimensions(element)
      hideOverflow(element)
      forceRepaint(element)
      setTransition(element)
      setOpenedDimensions(element)
    }
    const afterEnter = (element) => {
      unsetTransition(element)
      unsetOverflow(element)
      unsetDimensions(element)
      clearCachedDimensions()
    }
    const detectAndCacheDimensions = (element) => {
      // Cache actual dimensions
      // only once to void invalid values when
      // triggering during a transition
      if (cachedStyles.value) return;
      const visibility = element.style.visibility;
      const display = element.style.display;
      // Trick to get the width and
      // height of a hidden element
      element.style.visibility = "hidden";
      element.style.display = "";
      cachedStyles.value = { height: element.offsetHeight + "px" };
      // Restore any original styling
      element.style.visibility = visibility;
      element.style.display = display;
    }
    const clearCachedDimensions = () => {
      cachedStyles.value = null;
    }
    const setTransition = (el) => {
      el.style.transition = transition.value;
    }
    const unsetTransition = (el) => {
      el.style.transition = "";
    }
    const hideOverflow = (el) => {
      el.style.overflow = "hidden";
    }
    const unsetOverflow = (el) => {
      el.style.overflow = "";
    }
    const setClosedDimensions = (el) => {
      Object.keys(cachedStyles.value).forEach((key) => {
        el.style[key] = "0";
      });
    }
    const setOpenedDimensions = (el) => {
      Object.keys(cachedStyles.value).forEach((key) => {
        el.style[key] = cachedStyles.value[key];
      });
    }
    const unsetDimensions = (el) => {
      Object.keys(cachedStyles.value).forEach((key) => {
        el.style[key] = "";
      });
    }
    const forceRepaint = (el) => {
      // Force repaint to make sure the animation is triggered correctly.
      getComputedStyle(el)['height'];
    }
    const convertToCssProperty = (style) => {
      // Example: convert 'paddingTop' to 'padding-top'
      const upperChars = style.match(/([A-Z])/g);
      if (!upperChars) {
        return style;
      }
      for (let i = 0, n = upperChars.length; i < n; i++) {
        style = style.replace(
          new RegExp(upperChars[i]),
          "-" + upperChars[i].toLowerCase()
        );
      }
      if (style.slice(0, 1) === "-") {
        style = style.slice(1);
      }
      return style;
    }
    watch(
      () => props.triggerExpand,
      (newVal) => {
        setPanelExpandedStatus(idGroup.value, idPanel, newVal)
      }
    )
    onMounted(async () => {
      setPanelExpandedStatus(idGroup.value, idPanel, props.expanded)
    })
    setPanelExpandedStatus(idGroup.value, idPanel, props.expanded)
    return {
      body,
      collapseRef,
      bodyRef,
      bodyContentRef,
      isExpanded,
      afterEnter,
      afterLeave,
      collapse,
      expand,
      toggle,
      idGroup,
    }
  },
}
</script>

<style lang="scss" scoped>
.collapse {
  border-bottom: 1px solid $border; // v2 Itinerary Row States — warm divider (Wave 7 → seam, #2E2E2E dark)

  &:last-of-type {
    border-bottom: none;
  }
  // v2 Itinerary Row States — expanded row uses a green-tinted divider
  &--expanded {
    border-bottom-color: $row-expanded-border;
  }
  $root: &;
  &__header {
    pointer-events: none;
    transition: all .2s ease-out;

    #{$root}--expanded & {
      padding-top: 20px;
      padding-bottom: 20px;
    }
    #{$root}:first-child & {
      padding-top: 0;
    }

    .timeline #{$root}.first & {
      &:before {
        content: url('#{$assets-cdn}/images/tripplan/Start.svg');
        position: absolute;
        top: 20px;
        left: -32px;
        width: 24px;
        height: 32px;
      }
    }
    .timeline #{$root}.last & {
      &:before {
        content: url('#{$assets-cdn}/images/tripplan/End.svg');
        position: absolute;
        top: 20px;
        left: -32px;
        width: 24px;
        height: 32px;
      }
    }
    .timeline #{$root}:not(.first):not(.last) & {
      &:before {
        content: '';
        position: absolute;
        left: -28px;
        top: 20px;
        width: 16px;
        height: 16px;
        box-sizing: border-box;
        border: 2px solid $brand-green; // Wave 7 — retire off-brand blue → brand green (matches green-family row tints); §934
        background: $surface; // seam (#0D0D0D dark)
        border-radius: 100%;
        transition: all .25s ease-out;
      }
    }
    .timeline #{$root}--expanded:not(.first):not(.last) & {
      &:before {
        top: 40px;
        background: $brand-green; // Wave 7 — retire off-brand blue → brand green (expanded timeline node)
      }
    }
    #{$root}--expandable & {
      pointer-events: auto;
      cursor: pointer;
    }

    &-inner {
      transition: all .2s ease-out;
      .trip-plan #{$root} & {
        padding-left: 20px;
        padding-right: 20px;
      }
      .travel-guide #{$root} & {
        padding-left: 30px;
        padding-right: 30px;
      }
      &:hover {
        background: $row-hover-tint; // v2 Itinerary Row States — green-family hover (was off-brand $blue-branding-100)
      }
      #{$root}--expanded & {
        background: $row-expanded-tint; // v2 Itinerary Row States — green-tinted expanded header (was off-brand $blue-branding-100)
      }
    }
    &-title {
      flex: 1;
    }
    &-button {
      > .icon {
        transition: transform 0.25s ease-out;
      }
      #{$root}--expanded & {
        & > .icon {
          transform-origin: center;
          transform: rotate(-90deg);
          // svg {
          //   path {
          //     fill: $blue-branding;
          //   }
          // }
        }
      }
    }
  }

  &__body {
    overflow: hidden;
    transition: all 0.25s ease-out;
  }
  &__body-content {
    :deep(p:last-child) {
      margin-bottom: 0;
    }
    .trip-plan #{$root} & {
      padding-left: 23px;
      padding-right: 30px;
      padding-bottom: 30px;
      @include phone {
        padding-left: 30px;
      }
    }
    .travel-guide #{$root} & {
      padding: 10px 30px 30px;
    }
  }
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0.5;
}
:deep(a) {
  text-decoration: underline !important;
  color: $foreground; // Wave 7 → seam (near-white in dark; was $black)
}
</style>
